import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from "@angular/core";
import {WidgetModel} from "../../../models/widget.model";
import {DataSourceMetricColorEnum} from "../../../enums/data-source-metric-color.enum";
import {WidgetTypeEnum} from "../../../enums/widget-type.enum";
import {DashboardsService} from "../../services/dashboards.service";
import {WidgetDatasourceEnum} from "../../../enums/widget-datasource.enum";
import {DashboardModel} from "../../../models/dashboard.model";
import {DashboardAgentStatusEnum} from "../../../enums/dashboard-agent-status.enum";
import {DashboardServiceGroupEnum} from "../../../enums/dashboard-service-group.enum";
import * as _ from "lodash";
import * as moment from "moment";
import {DashboardWidgetConfigurationEnum} from "../../../enums/dashboard-widget-configuration.enum";
import {DataSourceConfigurationModel} from "../../../models/data-source-configuration.model";
import {SiteFiltersType} from "../../../../commons/types/site-filters.type";
import {DataSourceQueryMapEnum} from "../../../enums/data-source-query-map.enum";
import {DateRangeTemplateType} from "../../../../commons/types/date-range-template.type";
import {BranchReferenceModel} from "../../../../branches/models/branch-reference.model";
import {WidgetPredefinedFilterEnum} from "../../models/widget-predefined-filter.enum";
import {debounceTime, fromEvent, interval, Subject, Subscription} from "rxjs";
import {takeUntil} from "rxjs/operators";

require('./widget-container.scss');
@Component({
  standalone: false,
  selector: 'app-widget-container',
  templateUrl: './widget-container.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WidgetContainerComponent implements OnInit, OnChanges, OnDestroy {

  @Input() widget: WidgetModel;
  @Input() userBranches: BranchReferenceModel[];
  @Input() userTeams: any[];
  @Input() userBranchesIntersectionTeamMap: any;
  @Input() dashboard: DashboardModel;
  @Input() datasourceConfiguration: DataSourceConfigurationModel;
  @Input() dateRange: { dateStart: number, dateEnd: number };
  @Input() programTypes: string[];
  @Input() isHideActions: boolean = false;
  @Input() isHideRefreshDataButton: boolean = false;
  @Output() deleteWidgetEvent = new EventEmitter<null>();
  @Output() editWidgetEvent = new EventEmitter<null>();

  public data: any;
  public lastRefreshDate: number;
  // Lifecycle gate: ALL HTTP/interval streams in this component pipe takeUntil(_destroy$)
  // so in-flight requests/timers are cancelled on view destroy and never call detectChanges
  // / mutate state on a torn-down component.
  private readonly _destroy$ = new Subject<void>();
  public dataSourceMetricColorEnum: typeof DataSourceMetricColorEnum = DataSourceMetricColorEnum;
  public isRefreshButtonDisabled = false;
  public widgetTypeEnum: typeof WidgetTypeEnum = WidgetTypeEnum;
  public widgetDataSourceType: typeof WidgetDatasourceEnum = WidgetDatasourceEnum;
  public isLoadingData: boolean;
  public widgetPieChartSegmentsMap: any;
  public widgetPieChartTotalsSegments: any;
  public loadLimit: number;
  public tableCursor: string;
  public widgetDatasourceEnum: typeof WidgetDatasourceEnum = WidgetDatasourceEnum;
  public dashboardAgentStatusEnum: typeof DashboardAgentStatusEnum = DashboardAgentStatusEnum;
  public dashboardServiceGroupEnum: typeof DashboardServiceGroupEnum = DashboardServiceGroupEnum;
  public widgetElementSize: number;
  public dateRangeTemplate: any;
  public dashboardWidgetConfiguration: typeof DashboardWidgetConfigurationEnum = DashboardWidgetConfigurationEnum;
  public canSearch: boolean;
  public queryParams: any;
  public bringozFilterOptionIdsMap: any;
  public includeFilters: any;
  public dataSourceQueryMapEnum: typeof DataSourceQueryMapEnum = DataSourceQueryMapEnum;
  public widgetFilters: any;
  public widgetPredefinedFilter: WidgetPredefinedFilterEnum;
  public widgetPredefinedFilterTypes: typeof WidgetPredefinedFilterEnum = WidgetPredefinedFilterEnum;
  public resizeSubscription: Subscription;
  public windowWidth: number;


  constructor(@Inject(DashboardsService) private dashboardsService: DashboardsService, @Inject("dashboardsDialogsService") private dashboardsDialogsService,
              @Inject("dateRangeTimeFiltersService") private dateRangeTimeFiltersService, @Inject('dashboardsAlertsService') private dashboardsAlertsService,
              @Inject(ChangeDetectorRef) private cdr: ChangeDetectorRef, @Inject('splitService') private splitService) {
  }

  ngOnInit() {

    this.windowWidth = window.innerWidth;
    this.bringozFilterOptionIdsMap = {};
    this.widgetPredefinedFilter = this.widget.predefinedFilter;

    if (this.dashboard.branchIds?.length) {
      this.bringozFilterOptionIdsMap.branchIds = this.dashboard.branchIds;
    }

    this.includeFilters = this.datasourceConfiguration.filters || [];


    if (this.datasourceConfiguration.filters?.includes(SiteFiltersType.branchIds)) {
      this.includeFilters = [...this.includeFilters, SiteFiltersType.groups];
    }

    if (this.datasourceConfiguration.filters?.includes(SiteFiltersType.agentIds) && !this.includeFilters.includes(SiteFiltersType.teamIds)) {
      const agentIdx = this.includeFilters.indexOf(SiteFiltersType.agentIds);
      this.includeFilters.splice(agentIdx, 0, SiteFiltersType.teamIds);
    }

    if (this.datasourceConfiguration.filters?.includes(SiteFiltersType.dateRangeTemplateType)) {
      this.bringozFilterOptionIdsMap.dateRangeTemplateType = this.datasourceConfiguration.dateRangeTemplateOptions;
    }

    if (!this.splitService.getExposeAgentBranchesOnTheService() && this.datasourceConfiguration.filters?.includes(SiteFiltersType.agentBranchIds)) {
      const agentBranchIdsFilterIndex = this.includeFilters.findIndex(f => f === SiteFiltersType.agentBranchIds);
      this.includeFilters.splice(agentBranchIdsFilterIndex, 1);

      if (!!this.widget.filters && !!this.widget.filters['agentBranchIds']?.length) {
        delete this.widget.filters['agentBranchIds'];
      }
    }

    this.canSearch = this.datasourceConfiguration?.filters.includes(SiteFiltersType.query);
    // this.canSearch = true;

    this.widgetFilters = {...this.widget.filters};

    this.loadLimit = this.dashboardWidgetConfiguration[this.widget.datasource][this.widget.widgetType].loadLimit || 10;
    if (this.widget.filters.dateRangeTemplateType) {
      this.dateRangeTemplate = this.dateRangeTimeFiltersService.getDateRangeFilterByType(this.widget.filters.dateRangeTemplateType);
    }

    this.setWidgetDateRange();

    this.fetchData();
    if (!!this.widget.refreshInterval) {
      interval(this.widget.refreshInterval * 60 * 1000)
        .pipe(takeUntil(this._destroy$))
        .subscribe(() => this.fetchData(false));
    }


    if (this.widget.widgetType === this.widgetTypeEnum.PieChartWidget) {
      this.resizeSubscription = fromEvent(window, 'resize')
        .pipe(debounceTime(100))
        .subscribe(() => {
          if (window.innerWidth > 1024 && this.windowWidth < 1024 || window.innerWidth < 1024 && this.windowWidth > 1024) {
            this.windowWidth = window.innerWidth;
            this.initPieChartWidgetSegments();
          } else {
            this.windowWidth = window.innerWidth;
          }
        });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    const isDateRangeChanged = changes.dateRange && !changes.dateRange.isFirstChange() && this.canFilterBy(SiteFiltersType.dateRangeTemplateType);
    const isProgramTypesChanged = changes.programTypes && !changes.programTypes.isFirstChange() && this.canFilterBy(SiteFiltersType.programTypes);

    if (isDateRangeChanged) {
      this.setWidgetDateRange();
    }

    if (isDateRangeChanged || isProgramTypesChanged) {
      this.fetchData();
    }
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();

    if (this.resizeSubscription) {
      this.resizeSubscription.unsubscribe();
    }
  }


  onDeleteWidget() {
    this.deleteWidgetEvent.emit();
  }

  onEditWidget() {
    this.editWidgetEvent.emit();
  }

  onQueryChange(query) {
    this.queryParams.query = query;
    this.fetchData();
  }

  onFiltersChange(filters) {
    this.widgetFilters = {...filters};

    if (!this.widgetFilters.dateRangeTemplateType) {
      this.widgetFilters.dateRangeTemplateType = DateRangeTemplateType.TODAY;
    }

    this.dateRangeTemplate = this.dateRangeTimeFiltersService.getDateRangeFilterByType(this.widgetFilters.dateRangeTemplateType);
    this.setWidgetDateRange();

    this.fetchData();
  }

  fetchNextTableWidgetData() {
    if (!this.tableCursor || this.isLoadingData) {
      return;
    }

    this.isLoadingData = true;

    const queryParams: any = {
      ...this.getQueryParams(),
      limit: this.loadLimit,
      cursor: this.tableCursor
    }

    this.getTableWidgetData(queryParams, true);
  }

  onMetricValueClick(metricValue?) {
    if (!this.data[metricValue] && (this.widget.widgetType !== this.widgetTypeEnum.MetricsAverageWidget)) {
      return;
    }
    if (this.widget.datasource === this.widgetDatasourceEnum.agentsStatusTotal) {
      const filters = {
        ...this.getQueryParams(),
        status: this.dashboardAgentStatusEnum[metricValue]
      };
      this.dashboardsDialogsService.viewAgentsByFilters(filters, this.data[metricValue]);
    }

    if (this.widget.datasource === this.widgetDatasourceEnum.servicesStatusTotals) {
      if (!this.dashboardServiceGroupEnum[metricValue]) {
        return;
      }
      const filters = {
        ...this.getQueryParams(),
        statusGroup: this.dashboardServiceGroupEnum[metricValue]
      };
      this.dashboardsDialogsService.viewServicesByFilters(filters, this.data[metricValue]);
    }

    // if (this.widget.datasource === this.widgetDatasourceEnum.agentsRoutesScore) {
    //   const filters = {
    //     ...this.widget.filters,
    //     from: this.dateRangeTemplate?.dateStart ? this.dateRangeTemplate.dateStart : moment().startOf('day').valueOf(),
    //     to: this.dateRangeTemplate?.dateEnd ? this.dateRangeTemplate.dateEnd : moment().endOf('day').valueOf(),
    //   };
    //
    //   // const filters = {};
    //   this.dashboardsDialogsService.viewAgentsRouteScoreByFilters(filters);
    // }
  }

  fetchData(isLoadingData: boolean = true) {
    this.isLoadingData = isLoadingData;

    if (this.widget.widgetType === this.widgetTypeEnum.TableWidget) {
      const queryParams: any = {
        ...this.getQueryParams(),
        limit: this.loadLimit,
      }
      this.getTableWidgetData(queryParams);
    } else {
      this.getData(this.getQueryParams());
    }
  }

  private getTableWidgetData(queryParams, isFetchNext?: boolean) {
    this.isRefreshButtonDisabled = true;

    Object.keys(queryParams).forEach(key => queryParams[key] === undefined && delete queryParams[key]);

    if (this.widget.datasource === WidgetDatasourceEnum.agentsRoutesScore) {
      this.dashboardsService.getDashboardWidgetDataPost(this.widget.datasource, queryParams)
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: (res) => {
            if (isFetchNext && this.data.length) {
              this.data = [...this.data, ...res.list];
            } else {
              this.data = res.list
            }

            this.tableCursor = res.cursor;
            this.lastRefreshDate = moment().valueOf();
          },
          error: (err) => {
            this.isLoadingData = false;
            this.isRefreshButtonDisabled = false;
            this.dashboardsAlertsService.getDashboardWidgetDataError(this.widget.name)
              .then((res) => {
                if (res) {
                  this.getTableWidgetData(queryParams, isFetchNext);
                }
              });
            this.cdr.detectChanges();
          },
          complete: () => {
            this.isRefreshButtonDisabled = false;
            this.isLoadingData = false;
            this.cdr.detectChanges();
          }
        })
    } else {
      this.dashboardsService.getDashboardWidgetData(this.widget.datasource, queryParams)
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: (res) => {
            if (isFetchNext && this.data.length) {
              this.data = [...this.data, ...res.list];
            } else {
              this.data = res.list
            }

            this.tableCursor = res.cursor;
            this.lastRefreshDate = moment().valueOf();
          },
          error: (err) => {
            this.isLoadingData = false;
            this.isRefreshButtonDisabled = false;
            this.dashboardsAlertsService.getDashboardWidgetDataError(this.widget.name)
              .then((res) => {
                if (res) {
                  this.getTableWidgetData(queryParams, isFetchNext);
                }
              });
            this.cdr.detectChanges();
          },
          complete: () => {
            this.isRefreshButtonDisabled = false;
            this.isLoadingData = false;
            this.cdr.detectChanges();
          }
        })
    }

  }

  private getData(queryParams) {
    this.isRefreshButtonDisabled = true;

    Object.keys(queryParams).forEach(key => queryParams[key] === undefined && delete queryParams[key]);

    if (this.widget.datasource === WidgetDatasourceEnum.totalRoutesScore || this.widget.datasource === WidgetDatasourceEnum.agentsRoutesScore ||
      this.widget.datasource === WidgetDatasourceEnum.uniqueServiceFieldCount) {
      this.dashboardsService.getDashboardWidgetDataPost(this.widget.datasource, queryParams, this.getCounterWidgetQueryParams())
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: (data) => {
            this.data = data;
            this.lastRefreshDate = moment().valueOf();

            if (this.widget.widgetType === this.widgetTypeEnum.PieChartWidget) {
              this.initPieChartWidgetSegments();
            }

            if (this.widget.widgetType === this.widgetTypeEnum.MetricsAverageWidget) {
              this.initPieChartTotalsWidgetSegments();
            }
          },
          error: (err) => {
            this.isLoadingData = false;
            this.isRefreshButtonDisabled = false;
            this.dashboardsAlertsService.getDashboardWidgetDataError(this.widget.name)
              .then((res) => {
                if (res) {
                  this.getData(queryParams);
                }
              });
            this.cdr.detectChanges();
          },
          complete: () => {
            this.isRefreshButtonDisabled = false;
            this.isLoadingData = false;
            this.cdr.detectChanges();
          }
        })
    } else {
      this.dashboardsService.getDashboardWidgetData(this.widget.datasource, queryParams)
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: (data) => {
            this.data = data;
            this.lastRefreshDate = moment().valueOf();

            if (this.widget.widgetType === this.widgetTypeEnum.PieChartWidget) {
              this.initPieChartWidgetSegments();
            }

            if (this.widget.widgetType === this.widgetTypeEnum.MetricsAverageWidget) {
              this.initPieChartTotalsWidgetSegments();
            }
          },
          error: (err) => {
            this.isLoadingData = false;
            this.isRefreshButtonDisabled = false;
            this.dashboardsAlertsService.getDashboardWidgetDataError(this.widget.name)
              .then((res) => {
                if (res) {
                  this.getData(queryParams);
                }
              });
            this.cdr.detectChanges();
          },
          complete: () => {
            this.isRefreshButtonDisabled = false;
            this.isLoadingData = false;
            this.cdr.detectChanges();
          }
        })
    }

  }

  private initPieChartWidgetSegments() {
    this.widgetPieChartSegmentsMap = {};
    for (const [metricKey, metricValues] of Object.entries(this.widget.metrics)) {
      this.widgetPieChartSegmentsMap[metricKey] = {};
      metricValues.forEach(mv => {

        this.widgetPieChartSegmentsMap[metricKey][mv.metricValue] = {};
        this.widgetPieChartSegmentsMap[metricKey][mv.metricValue].innerTextConfiguration = {
          color: this.dataSourceMetricColorEnum[this.widget.datasource][metricKey][mv.metricValue],
          text: this.windowWidth <= 1024 || this.widget.extraData?.displayType === 'percentage' ? ((!!this.data?.total ? (this.data[mv.metricValue] / this.data.total) : 0) * 100).toFixed(1) + '%' : (this.data[mv.metricValue] + '/' + (this.data?.total ?? 0))
        }

        this.widgetPieChartSegmentsMap[metricKey][mv.metricValue].segments = [
          {
            value: this.data[mv.metricValue],
            color: this.dataSourceMetricColorEnum[this.widget.datasource][metricKey][mv.metricValue]
          },
          {
            value: (this.data?.total ?? 0) - this.data[mv.metricValue],
            color: this.addAlphaToRgb(this.dataSourceMetricColorEnum[this.widget.datasource][metricKey][mv.metricValue], 0.3)
          }
        ]
      })
    }
  }

  private initPieChartTotalsWidgetSegments() {
    this.widgetPieChartTotalsSegments = {
      innerTextConfiguration: undefined,
      segments: []
    };

    const pieChartKeys = Object.keys(this.data);
    const averageScore = Math.round((_.reduce(pieChartKeys, (scoreSum, key: string) => {
      return scoreSum + this.data[key];
    }, 0) || 0) / pieChartKeys.length);

    const averageScoreSegment = {
      value: averageScore,
      color: 'rgb(49, 139, 49)'
    }
    this.widgetPieChartTotalsSegments.innerTextConfiguration = {
      color: 'rgb(49, 139, 49)',
      text: averageScore + '%'
    }
    this.widgetPieChartTotalsSegments.segments.push(averageScoreSegment);

    const averageScoreSegmentDiff = {
      value: 100 - averageScore,
      color: this.addAlphaToRgb('rgb(49, 139, 49)', 0.3)
    }

    this.widgetPieChartTotalsSegments.segments.push(averageScoreSegmentDiff);

  }

  private addAlphaToRgb(rgb, alpha) {
    const [r, g, b] = rgb.match(/\d+/g).map(Number);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }


  // The dashboard date range (when selected) overrides the widget's own date range template, if the widget supports date filtering
  private setWidgetDateRange() {
    const range = (this.canFilterBy(SiteFiltersType.dateRangeTemplateType) && this.dateRange) || this.dateRangeTemplate;
    this.widgetFilters.from = range?.dateStart || moment().startOf('day').valueOf();
    this.widgetFilters.to = range?.dateEnd || moment().endOf('day').valueOf();
  }

  // Dashboard filters apply only to widgets whose datasource supports filtering by them
  private canFilterBy(filter: SiteFiltersType) {
    return !!this.datasourceConfiguration.filters?.includes(filter);
  }

  private getQueryParams() {
    const params: { [key: string]: any } = {
      ...this.widgetFilters,
      // branchIds: !!this.widgetFilters.branchIds?.length ? this.widgetFilters.branchIds : (!!this.widget.filters?.branchIds?.length ? this.widget.filters.branchIds : (this.dashboard.branchIds?.length ? this.dashboard.branchIds : [])),
      // query: undefined
    }

    if (this.programTypes?.length && this.canFilterBy(SiteFiltersType.programTypes)) {
      params.programTypes = this.programTypes;
    }

    if (params.omsOrderStatuses) {
      _.set(params, 'status', params.omsOrderStatuses);
      _.unset(params, 'omsOrderStatuses');
    }

    if (params.fulfillmentDisplayStatus) {
      _.set(params, 'displayStatus', _.compact(params.fulfillmentDisplayStatus));
      _.unset(params, 'fulfillmentDisplayStatus');
    }

    this.setBranchesOnQueryParams(params);
    this.setAgentsOnQueryParams(params)

    Object.keys(params).forEach(key => (params[key] === undefined || params[key] === null) && delete params[key]);

    return params;
  }

  // The counter widget's selected service field is sent as a query param, while the filters go in the body
  private getCounterWidgetQueryParams() {
    const counterField = this.widget.metrics?.serviceField?.[0]?.metricValue;
    return counterField ? {counterField} : undefined;
  }

  private setBranchesOnQueryParams(queryParams) {
    if (this.widgetFilters.branchIds?.length) {
      queryParams.branchIds = this.widgetFilters.branchIds;
    } else if (!!this.widgetPredefinedFilter) {
      this.setPredefinedFilterBranches(queryParams);
    } else {
      queryParams.branchIds = this.dashboard.branchIds;
    }
  }

  private setAgentsOnQueryParams(queryParams) {
    if (this.widgetFilters.agentIds?.length) {
      queryParams.agentIds = this.widgetFilters.agentIds;
    } else if (!!this.widgetPredefinedFilter) {
      this.setPredefinedFilterTeams(queryParams);
    }
  }

  private setPredefinedFilterBranches(queryParams) {
    const userBranchIds = this.userBranches?.map(b => b.id);
    if (this.widgetPredefinedFilter === this.widgetPredefinedFilterTypes.branches) {
      queryParams.branchIds = userBranchIds;
    } else if (this.widgetPredefinedFilter === this.widgetPredefinedFilterTypes.branchesIntersectionTeams && !this.widgetFilters.agentIds?.length) {
      queryParams.branchIds = this.userBranchesIntersectionTeamMap.branchIds;
    } else if (this.widgetPredefinedFilter === this.widgetPredefinedFilterTypes.teamsExcludeBranches) {
      queryParams.excludedBranchIds = userBranchIds;
    } else if (this.widgetPredefinedFilter === this.widgetPredefinedFilterTypes.branchesExcludeTeams) {
      queryParams.branchIds = userBranchIds;
    } else if (this.dashboard.branchIds) {
      queryParams.branchIds = this.dashboard.branchIds;
    }
  }

  private setPredefinedFilterTeams(queryParams) {
    const userTeamAgentIds = _.uniqBy(_.flatMap(this.userTeams, 'agents'), 'id').map(a => a.id);
    if (this.widgetPredefinedFilter === this.widgetPredefinedFilterTypes.teams) {
      queryParams.agentIds = userTeamAgentIds;
    } else if (this.widgetPredefinedFilter === this.widgetPredefinedFilterTypes.branchesIntersectionTeams && !this.widgetFilters.branchIds?.length) {
      queryParams.agentIds = this.userBranchesIntersectionTeamMap.agentIds;
    } else if (this.widgetPredefinedFilter === this.widgetPredefinedFilterTypes.branchesExcludeTeams ||
      this.widgetPredefinedFilter === this.widgetPredefinedFilterTypes.excludeTeams) {
      queryParams.excludedAgentIds = userTeamAgentIds;
    } else if (this.widgetPredefinedFilter === this.widgetPredefinedFilterTypes.teamsExcludeBranches) {
      queryParams.agentIds = userTeamAgentIds;
    }
  }

}
